import { useQuery } from '@tanstack/react-query';
import type { QuotationPipelineSummary, QuotationRow } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { Badge } from '@/components/Badge';
import type { BadgeTone } from '@/components/Badge';
import { Skeleton } from '@/components/States';
import { KpiCard } from '@/components/KpiCard';
import { fetchQuotations, fetchQuotationsList, reportKeys } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);

/**
 * Raised/converted/expired are independent subsets of the same range, not
 * mutually exclusive buckets — a quotation converted to a sale counts under
 * BOTH "raised" (it was issued in range) and "converted" (it also closed in
 * range), so the three counts are not expected to sum to `raised`.
 */
function isEmptyPipeline(summary: QuotationPipelineSummary): boolean {
  return summary.raised === 0 && summary.converted === 0 && summary.expired === 0;
}

const STATUS_TONE: Record<string, BadgeTone> = {
  Draft: 'neutral',
  Converted: 'success',
  Expired: 'danger',
};

const FILTER_FIELDS = ['Customer', 'Quotation #', 'Status'] as const;
const TOGGLEABLE = ['customerName', 'expiryDate', 'grandTotal', 'status'] as const;
const COLUMN_LABELS: Record<string, string> = {
  customerName: 'Customer',
  expiryDate: 'Expiry',
  grandTotal: 'Total',
  status: 'Status',
};

const QUOTATION_COLUMNS: Column<QuotationRow>[] = [
  { key: 'quotationNumber', header: 'Quotation #', pinned: true, cell: (row) => row.quotationNumber },
  { key: 'date', header: 'Date', pinned: true, cell: (row) => row.date },
  { key: 'customerName', header: 'Customer', cell: (row) => row.customerName || '—' },
  { key: 'expiryDate', header: 'Expiry', cell: (row) => row.expiryDate || '—' },
  { key: 'grandTotal', header: 'Total', align: 'right', cell: (row) => money(row.grandTotal) },
  {
    key: 'status',
    header: 'Status',
    cell: (row) => (
      <Badge tone={STATUS_TONE[row.status] ?? 'neutral'} dot>
        {row.status}
      </Badge>
    ),
  },
];

export function QuotationsReportPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const layout = useReportLayout('quotations', TOGGLEABLE);
  const columns = applyReportLayout(QUOTATION_COLUMNS, layout);

  const pipelineQuery = useQuery({
    queryKey: reportKeys.quotations(filters),
    queryFn: ({ signal }) => fetchQuotations(filters, signal),
  });
  const listQuery = useQuery({
    queryKey: reportKeys.quotationsList(filters),
    queryFn: ({ signal }) => fetchQuotationsList(filters, signal),
  });

  return (
    <div>
      <PageHeader title="Quotations" description="Pipeline of quotations raised, converted, and expired in the selected window." />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <ReportToolbar>
        <ReportPeriodButton
          from={filters.from}
          to={filters.to}
          onChange={toolbar.setRange}
          deselected={toolbar.filterActive}
        />
      </ReportToolbar>
      <AsyncSection
        state={pipelineQuery}
        isEmpty={isEmptyPipeline}
        emptyTitle="No quotations in this window"
        skeleton={
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        }
      >
        {(summary) => (
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard label="Raised" value={`${summary.raised} · ${money(summary.raisedValue)}`} />
            <KpiCard label="Converted" value={`${summary.converted} · ${money(summary.convertedValue)}`} />
            <KpiCard label="Expired" value={`${summary.expired} · ${money(summary.expiredValue)}`} />
          </div>
        )}
      </AsyncSection>

      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search quotation # or customer"
        />
        <ReportFiltersButton
          fields={FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Status' ? 'Draft / Converted / Expired' : field === 'Customer' ? 'name' : 'quotation #'
          }
        />
        <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
      </ReportToolbar>
      <AsyncSection
        state={listQuery}
        select={(rows: QuotationRow[]) =>
          applyClientFilters(rows, {
            search: toolbar.search,
            filterRows: toolbar.filterRows,
            matchAny: toolbar.matchAny,
            fieldValue: (row, field) =>
              field === 'Customer'
                ? row.customerName
                : field === 'Quotation #'
                  ? row.quotationNumber
                  : field === 'Status'
                    ? row.status
                    : undefined,
            searchValues: (row) => [row.quotationNumber, row.customerName],
          })
        }
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No quotations match"
        skeleton={<TableSkeleton columns={6} />}
      >
        {(rows) => (
          <DataTable
            caption="Quotations"
            layout={layout}
            columns={columns}
            rows={rows}
            rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
            totals={{ label: 'Total', cells: { grandTotal: money(rows.reduce((acc, row) => acc + row.grandTotal, 0)) } }}
          />
        )}
      </AsyncSection>
    </div>
  );
}
